import { customElement, scss } from '../../core/ids-decorators';
import { attributes } from '../../core/ids-attributes';
import { stringToBool, stringToNumber } from '../../utils/ids-string-utils/ids-string-utils';

import IdsElement from '../../core/ids-element';

import styles from './ids-layout-flex.scss';

import './ids-layout-flex-item';

// List of flex options
export const FLEX_OPTIONS = {
  alignContent: ['flex-start', 'flex-end', 'center', 'space-between', 'space-around', 'space-evenly', 'stretch', 'start', 'end', 'baseline'],
  alignItems: ['start', 'end', 'flex-start', 'flex-end', 'center', 'center-inputs', 'baseline', 'stretch'],
  direction: ['row', 'row-reverse', 'column', 'column-reverse'],
  display: ['flex', 'inline-flex'],
  justifyContent: ['start', 'stretch', 'end', 'flex-start', 'flex-end', 'center', 'left', 'right', 'space-between', 'space-around', 'space-evenly'],
  wrap: ['nowrap', 'wrap', 'wrap-reverse'],
  units: [0, 1, 2, 4, 8, 12, 16, 20, 24, 28, 32, 36, 40],
  // Named spacing tokens mirror ids-layout-grid so the two components are interchangeable.
  gapSizes: ['none', 'xs', 'sm', 'md', 'lg', 'xl'],
  paddingSizes: ['xxs', 'xs', 'sm', 'md', 'lg', 'xl'],
  marginSizes: ['xs', 'sm', 'md', 'lg', 'xl', 'auto']
};

/**
 * IDS Layout Flex Component
 * @type {IdsLayoutFlex}
 * @inherits IdsElement
 */
@customElement('ids-layout-flex')
@scss(styles)
export default class IdsLayoutFlex extends IdsElement {
  constructor() {
    super();
  }

  static get attributes(): any {
    return [
      ...super.attributes,
      attributes.ALIGN_CONTENT,
      attributes.ALIGN_ITEMS,
      attributes.DIRECTION,
      attributes.DISPLAY,
      attributes.FILL,
      attributes.GAP,
      attributes.GAP_X,
      attributes.GAP_Y,
      attributes.HEIGHT,
      attributes.JUSTIFY_CONTENT,
      attributes.MARGIN,
      attributes.MARGIN_Y,
      attributes.PADDING,
      attributes.PADDING_X,
      attributes.PADDING_Y,
      attributes.WRAP
    ];
  }

  #childObserver?: MutationObserver;

  connectedCallback() {
    super.connectedCallback();
    this.#syncChildFieldHeight();
    this.#observeChildren();
  }

  disconnectedCallback() {
    super.disconnectedCallback?.();
    this.#childObserver?.disconnect();
    this.#childObserver = undefined;
  }

  /**
   * For align-items='center-inputs': inspect descendant inputs for their
   * field-height / compact setting and reflect it onto this host so the
   * ids-checkbox can size itself to match via :host-context selectors.
   */
  #syncChildFieldHeight(): void {
    const current = this.getAttribute(attributes.FIELD_HEIGHT);

    if (this.alignItems !== 'center-inputs') {
      if (current !== null) this.removeAttribute(attributes.FIELD_HEIGHT);
      return;
    }

    const inputs = this.querySelectorAll<HTMLElement>(
      'ids-input, ids-date-picker, ids-time-picker, ids-dropdown, ids-textarea, ids-trigger-field'
    );
    let detected: string | null = null;
    inputs.forEach((el) => {
      if (detected) return;
      const fh = el.getAttribute(attributes.FIELD_HEIGHT);
      if (fh) {
        detected = fh;
      } else if (el.hasAttribute(attributes.COMPACT)) {
        detected = 'xs';
      }
    });

    if (detected) {
      if (current !== detected) this.setAttribute(attributes.FIELD_HEIGHT, detected);
    } else if (current !== null) {
      this.removeAttribute(attributes.FIELD_HEIGHT);
    }
  }

  #observeChildren(): void {
    this.#childObserver = new MutationObserver((records) => {
      // Ignore mutations on the host itself to avoid feedback loops when
      // we reflect the inferred field-height back onto this element.
      const fromChild = records.some((r) => r.target !== this);
      if (fromChild) this.#syncChildFieldHeight();
    });
    this.#childObserver.observe(this, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: [attributes.FIELD_HEIGHT, attributes.COMPACT]
    });
  }

  template(): string {
    return `<slot></slot>`;
  }

  /**
   * Set the align content setting
   * @param {string} value The value
   */
  set alignContent(value: string | null) {
    if (value && FLEX_OPTIONS.alignContent.includes(value)) {
      this.setAttribute(attributes.ALIGN_CONTENT, value);
    } else {
      this.removeAttribute(attributes.ALIGN_CONTENT);
    }
  }

  get alignContent() { return this.getAttribute(attributes.ALIGN_CONTENT); }

  /**
   * Set the align items setting
   * @param {string} value The value
   */
  set alignItems(value: string | null) {
    if (value && FLEX_OPTIONS.alignItems.includes(value)) {
      this.setAttribute(attributes.ALIGN_ITEMS, value);
    } else {
      this.removeAttribute(attributes.ALIGN_ITEMS);
    }
    this.#syncChildFieldHeight();
  }

  get alignItems() { return this.getAttribute(attributes.ALIGN_ITEMS); }

  /**
   * Set the direction setting
   * @param {string} value The value
   */
  set direction(value: string | null) {
    if (value && FLEX_OPTIONS.direction.includes(value)) {
      this.setAttribute(attributes.DIRECTION, value);
    } else {
      this.removeAttribute(attributes.DIRECTION);
    }
  }

  get direction() { return this.getAttribute(attributes.DIRECTION); }

  /**
   * Set the display setting
   * @param {string} value The value
   */
  set display(value: string | null) {
    if (value && FLEX_OPTIONS.display.includes(value)) {
      this.setAttribute(attributes.DISPLAY, value);
    } else {
      this.removeAttribute(attributes.DISPLAY);
    }
  }

  get display() { return this.getAttribute(attributes.DISPLAY); }

  /**
   * Validates a gap value (named token or pixel unit) and writes/removes the attribute.
   * @param {string} attr The attribute name
   * @param {number | string | null} value The gap value to validate and set
   */
  #setGapAttribute(attr: string, value: number | string | null): void {
    if (typeof value === 'string' && FLEX_OPTIONS.gapSizes.includes(value)) {
      this.setAttribute(attr, value);
      return;
    }
    const val = stringToNumber(value);
    if (!Number.isNaN(val) && FLEX_OPTIONS.units.includes(val)) {
      this.setAttribute(attr, String(val));
    } else {
      this.removeAttribute(attr);
    }
  }

  /**
   * Set the gap, apply same for both horizontal and vertical sides
   * @param {number|string} value Named token ('none', 'xs', 'sm', 'md', 'lg', 'xl') or pixel unit
   */
  set gap(value: number | string | null) {
    this.#setGapAttribute(attributes.GAP, value);
  }

  get gap() { return this.getAttribute(attributes.GAP); }

  /**
   * Set the horizontal gap
   * @param {number|string} value Named token ('none', 'xs', 'sm', 'md', 'lg', 'xl') or pixel unit
   */
  set gapX(value: number | string | null) {
    this.#setGapAttribute(attributes.GAP_X, value);
  }

  get gapX() { return this.getAttribute(attributes.GAP_X); }

  /**
   * Set card vertical gap
   * @param {number|string} value Named token ('none', 'xs', 'sm', 'md', 'lg', 'xl') or pixel unit
   */
  set gapY(value: number | string | null) {
    this.#setGapAttribute(attributes.GAP_Y, value);
  }

  get gapY() { return this.getAttribute(attributes.GAP_Y); }

  /**
   * Set the padding (all sides). Accepts named tokens that mirror ids-layout-grid.
   * @param {string|null} value 'xxs', 'xs', 'sm', 'md', 'lg', 'xl', or null
   */
  set padding(value: string | null) {
    if (value && FLEX_OPTIONS.paddingSizes.includes(value)) {
      this.setAttribute(attributes.PADDING, value);
    } else {
      this.removeAttribute(attributes.PADDING);
    }
  }

  get padding() { return this.getAttribute(attributes.PADDING); }

  /**
   * Set the horizontal padding (padding-inline).
   * @param {string|null} value 'xxs', 'xs', 'sm', 'md', 'lg', 'xl', or null
   */
  set paddingX(value: string | null) {
    if (value && FLEX_OPTIONS.paddingSizes.includes(value)) {
      this.setAttribute(attributes.PADDING_X, value);
    } else {
      this.removeAttribute(attributes.PADDING_X);
    }
  }

  get paddingX() { return this.getAttribute(attributes.PADDING_X); }

  /**
   * Set the vertical padding (padding-block).
   * @param {string|null} value 'xxs', 'xs', 'sm', 'md', 'lg', 'xl', or null
   */
  set paddingY(value: string | null) {
    if (value && FLEX_OPTIONS.paddingSizes.includes(value)) {
      this.setAttribute(attributes.PADDING_Y, value);
    } else {
      this.removeAttribute(attributes.PADDING_Y);
    }
  }

  get paddingY() { return this.getAttribute(attributes.PADDING_Y); }

  /**
   * Set the margin (all sides). Accepts named tokens that mirror ids-layout-grid.
   * @param {string|null} value 'xs', 'sm', 'md', 'lg', 'xl', 'auto', or null
   */
  set margin(value: string | null) {
    if (value && FLEX_OPTIONS.marginSizes.includes(value)) {
      this.setAttribute(attributes.MARGIN, value);
    } else {
      this.removeAttribute(attributes.MARGIN);
    }
  }

  get margin() { return this.getAttribute(attributes.MARGIN); }

  /**
   * Set the vertical margin (margin-block).
   * @param {string|null} value 'xs', 'sm', 'md', 'lg', 'xl', or null
   */
  set marginY(value: string | null) {
    if (value && FLEX_OPTIONS.marginSizes.includes(value) && value !== 'auto') {
      this.setAttribute(attributes.MARGIN_Y, value);
    } else {
      this.removeAttribute(attributes.MARGIN_Y);
    }
  }

  get marginY() { return this.getAttribute(attributes.MARGIN_Y); }

  /**
   * Set the height of the flex container
   * @param {string} value The height value (e.g.'200px')
   */
  set height(value: string | null) {
    if (value) {
      this.setAttribute(attributes.HEIGHT, value);
      this.style.height = value;
    } else {
      this.removeAttribute(attributes.HEIGHT);
      this.style.height = '';
    }
  }

  get height() { return this.getAttribute(attributes.HEIGHT); }

  /**
   * Set the justify content setting
   * @param {string} value The value
   */
  set justifyContent(value: string | null) {
    if (value && FLEX_OPTIONS.justifyContent.includes(value)) {
      this.setAttribute(attributes.JUSTIFY_CONTENT, value);
    } else {
      this.removeAttribute(attributes.JUSTIFY_CONTENT);
    }
  }

  get justifyContent() { return this.getAttribute(attributes.JUSTIFY_CONTENT); }

  /**
   * Set the wrap setting
   * @param {string} value The value
   */
  set wrap(value: string | null) {
    if (value && FLEX_OPTIONS.wrap.includes(value)) {
      this.setAttribute(attributes.WRAP, value);
    } else {
      this.removeAttribute(attributes.WRAP);
    }
  }

  get wrap() { return this.getAttribute(attributes.WRAP); }

  /**
   * Set flex container to 100% height
   * @param {boolean} val value
   */
  set fullHeight(val: boolean) {
    if (stringToBool(val)) {
      this.setAttribute(attributes.FULL_HEIGHT, '');
    } else {
      this.removeAttribute(attributes.FULL_HEIGHT);
    }
  }

  get fullHeight(): boolean {
    return this.hasAttribute(attributes.FULL_HEIGHT);
  }
}
